import * as Sentry from '@sentry/browser';

import { IMAGE_PROVIDER_TYPES } from 'js/config/consts';

import config, { DEFAULT_PAGE_SIZE } from '../../config/config';
import { getOptions, rendererHeaders } from '../helpers/app-services/AppServices/helpers';
import { rateLimitedfetch } from '../helpers/rateLimitedFetch';
import { fetchWithRefreshAuth } from '../helpers/requestLimiter';
import { appServices } from '../helpers/app-services/AppServices';
import { currentUserAccessToken } from '../helpers/authHelper';

const { LIBRARY_API_DOMAIN } = config;

// ugly fix: strip header to keep library cors happy
const fetchStripHeader = (url, options) => {
  delete options.headers['x-spk-app-name'];
  return rateLimitedfetch(url, options);
};
async function fetchWithReAuthLib(path, options) {
  return fetchWithRefreshAuth(
    fetchStripHeader,
    `${LIBRARY_API_DOMAIN}${path}`,
    appServices.exchangeCognitoTokenForSparkolToken,
    options
  ).then(res => {
    if (!res.ok) {
      const error = new Error(`Request failed with status ${res.status}`);
      error.status = res.status;
      throw error;
    }
    const accept = options.headers['Accept'] || options.headers['accept'];
    if (accept && accept.includes('application/json')) {
      return res.json();
    } else {
      return res.text();
    }
  });
}

export const getVisualStyleFacetValues = async () => {
  const options = getOptions();
  options.headers['Authorization'] = await currentUserAccessToken();
  return fetchWithReAuthLib('/api/vsc/library/facet-values/image-style', options);
};

export const getImageLibraries = async () => {
  const options = getOptions();
  options.headers['Authorization'] = await currentUserAccessToken();
  return fetchWithReAuthLib(`/api/vsc/libraries`, options);
};

export const getStreamlineGraphicsLibrary = async (libraryId, pagination) => {
  const DEFAULT_EMPTY_RESULT = {
    images: [],
    cursor: '0',
    nextOffset: 0,
    hasMore: false
  };

  const page = pagination?.find(p => p.source === 'streamline-graphics');

  if (page && !page.hasMore) {
    return DEFAULT_EMPTY_RESULT;
  }

  const offset = page ? page.nextOffset : 0;

  const options = getOptions();
  options.headers['Authorization'] = await currentUserAccessToken();
  const results = await fetchWithReAuthLib(
    `/api/vsc/streamline/library/${libraryId}?productType=elements&offset=${offset}&pageSize=${DEFAULT_PAGE_SIZE}`,
    options
  );

  return {
    ...results,
    pagination: [
      {
        source: 'streamline-graphics',
        nextOffset: results.nextOffset,
        hasMore: results.hasMore
      }
    ]
  };
};

export const getImageLibrary = async (libraryId, searchSource, cursor = '0', pagination) => {
  if (searchSource === 'videoscribe') {
    return getStreamlineAndImageLibrary(libraryId, pagination);
  }
  if (searchSource === 'streamline-graphics') {
    return getStreamlineGraphicsLibrary(libraryId, pagination);
  }
  const options = getOptions();
  options.headers['Authorization'] = await currentUserAccessToken();
  const url =
    searchSource === 'noun-project' ? `/api/vsc/noun-project/library/${libraryId}` : `/api/vsc/library/${libraryId}`;
  const library = await fetchWithReAuthLib(`${url}?cursor=${cursor}&pageSize=${DEFAULT_PAGE_SIZE}`, options);
  // Use cdn for thumbnails - no cdn in testing envs
  const s3BaseProduction = 'sparkol-assets-s3-production-libraries.s3.amazonaws.com';
  const cdnBaseProduction = 'cloud-files-cdn.sparkol.com/library/images/online';
  const libraryCdn = {
    ...library,
    images: library.images.map(image => ({
      ...image,
      bucketUrl: image.bucketUrl.replace(s3BaseProduction, cdnBaseProduction),
      isPremium: image.isPremium
    }))
  };
  return libraryCdn;
};

export const getStreamlineAndImageLibrary = async (libraryId, pagination) => {
  const DEFAULT_EMPTY_RESULT = {
    images: [],
    cursor: '0',
    nextOffset: 0,
    hasMore: false
  };

  const fetchLibrary = async source => {
    const options = getOptions();
    options.headers['Authorization'] = await currentUserAccessToken();
    const page = pagination?.find(p => p.source === source);
    const hasMore = page ? page.hasMore : true;

    if (!hasMore) {
      return DEFAULT_EMPTY_RESULT;
    }

    const query = source === 'streamline' ? `offset=${page?.nextOffset || 0}` : `cursor=${page?.cursor || '0'}`;
    const url = source === 'streamline' ? `/api/vsc/streamline/library/${libraryId}` : `/api/vsc/library/${libraryId}`;
    return fetchWithReAuthLib(`${url}?${query}&pageSize=${DEFAULT_PAGE_SIZE / 2}`, options).catch(err => {
      if (err.status) {
        Sentry.captureException(new Error(`${source} library fetch failed: ${err.status} - ${err.message}`));
      }
      throw err;
    });
  };

  const [streamlineResult, libraryResult] = await Promise.allSettled([
    fetchLibrary('streamline'),
    fetchLibrary('library')
  ]);

  if (streamlineResult.status === 'rejected' && libraryResult.status === 'rejected') {
    throw new Error(libraryResult.reason);
  }

  const streamlineResults = streamlineResult.status === 'fulfilled' ? streamlineResult.value : DEFAULT_EMPTY_RESULT;
  const libraryResults = libraryResult.status === 'fulfilled' ? libraryResult.value : DEFAULT_EMPTY_RESULT;

  return {
    cursor: libraryResults.cursor,
    images: [...libraryResults.images, ...streamlineResults.images],
    pagination: [
      {
        source: 'library',
        cursor: libraryResults.cursor,
        hasMore: libraryResults.hasMore
      },
      {
        source: 'streamline',
        nextOffset: streamlineResults.nextOffset,
        hasMore: streamlineResults.hasMore
      }
    ]
  };
};

export const getImageByIdFunction = type => {
  return async (id, thumb = false) => {
    if (type === IMAGE_PROVIDER_TYPES.STREAMLINE) {
      const standardOptions = getOptions('GET', undefined, 'image/svg+xml', false, undefined, 'image/svg+xml');
      const options = {
        ...standardOptions,
        headers: {
          ...standardOptions.headers,
          ...rendererHeaders(true)
        }
      };
      options.headers['Authorization'] = await currentUserAccessToken();
      return fetchWithReAuthLib(`/api/vsc/streamline/image/${id}`, options);
    }
    let url;
    if (type === IMAGE_PROVIDER_TYPES.NOUN_PROJECT) {
      url = `/api/vsc/noun-project/image/${id}`;
    } else {
      url = `/api/vsc/library/image/${id}`;
    }
    const standardOptions = getOptions();
    const options = {
      ...standardOptions,
      headers: {
        ...standardOptions.headers,
        ...rendererHeaders(true)
      }
    };
    options.headers['Authorization'] = await currentUserAccessToken();
    return fetchWithReAuthLib(url, options).then(({ data }) => {
      return `${data.bucketUrl}/${thumb ? data.thumbnailFilename : data.imageFilename}`;
    });
  };
};

const searchNounProject = async (searchTerms, nextPage) => {
  const options = getOptions('GET');
  const searchParams = new URLSearchParams({
    searchTerms: searchTerms.join(' ').toLowerCase(),
    ...(nextPage && { nextPage })
  });
  options.headers['Authorization'] = await currentUserAccessToken();
  return fetchWithReAuthLib(`/api/vsc/noun-project/search?${searchParams}`, options);
};

const buildStreamlineSearchRequest = async (query, { limit, offset, productType = null, filters = null }) => {
  const searchParams = new URLSearchParams({
    searchTerms: query.join(' ').toLowerCase(),
    limit: limit.toString(),
    offset: offset.toString()
  });
  if (productType) {
    searchParams.set('productType', productType);
  }
  if (filters) {
    const facetIds = Object.keys(filters);
    facetIds.forEach(facetId => {
      filters[facetId].forEach(value => {
        searchParams.append(facetId, value);
      });
    });
  }
  const options = getOptions('GET');
  options.headers['Authorization'] = await currentUserAccessToken();
  return { options, url: `/api/vsc/streamline/search?${searchParams}` };
};

const searchStreamlineGraphics = async (query, pagination) => {
  const page = pagination?.find(p => p.source === 'streamline-graphics');
  if (page && !page.hasMore) {
    return {
      data: [{ images: [] }],
      meta: { offset: 0, total: 0, nextOffset: 0, hasMore: false }
    };
  }
  const limit = DEFAULT_PAGE_SIZE;
  const offset = page ? page.nextOffset : 0;

  const { options, url } = await buildStreamlineSearchRequest(query, {
    limit,
    offset,
    productType: 'elements'
  });

  const searchResult = await fetchWithReAuthLib(url, options);
  return {
    ...searchResult,
    pagination: [
      {
        source: 'streamline-graphics',
        offset: searchResult.meta.offset,
        nextOffset: searchResult.meta.nextOffset,
        total: searchResult.meta.total,
        hasMore: searchResult.meta.hasMore
      }
    ]
  };
};

export const searchImageLibrary = async (
  imageService,
  search,
  _offset = 0,
  nextPage,
  filters,
  pagination,
  activeFilterSources = []
) => {
  const query = search
    .trim()
    .split(/[^\p{L}\p{N}']+/u)
    .filter(n => n);

  if (imageService === 'noun-project') {
    return searchNounProject(query, nextPage);
  }

  if (imageService === 'streamline-graphics') {
    return searchStreamlineGraphics(query, pagination);
  }

  return searchStreamlineAndImageLibrary(query, nextPage, filters, pagination, activeFilterSources);
};

export const searchStreamlineAndImageLibrary = async (
  query,
  nextPage,
  filters,
  pagination,
  activeFilterSources = []
) => {
  const DEFAULT_EMPTY_RESULT = {
    data: [{ images: [] }],
    meta: { offset: 0, total: 0, nextOffset: 0, hasMore: false }
  };
  const hasActiveFilters = activeFilterSources.length > 0;
  const skipLibrary = hasActiveFilters && !activeFilterSources.includes('library');
  const skipStreamline = hasActiveFilters && !activeFilterSources.includes('streamline');

  const fetchStreamlineSearch = async () => {
    if (skipStreamline) return DEFAULT_EMPTY_RESULT;

    const page = pagination?.find(p => p.source === 'streamline');
    if (page && !page.hasMore) {
      return DEFAULT_EMPTY_RESULT;
    }

    const offset = page ? page.nextOffset : 0;
    const { options, url } = await buildStreamlineSearchRequest(query, {
      limit: DEFAULT_PAGE_SIZE / 2,
      offset,
      filters
    });
    return fetchWithReAuthLib(url, options).catch(err => {
      if (err.status) {
        Sentry.captureException(new Error(`streamline search failed: ${err.status} - ${err.message}`));
      }
      throw err;
    });
  };

  const fetchLibrarySearch = async () => {
    if (skipLibrary) return DEFAULT_EMPTY_RESULT;

    const page = pagination?.find(p => p.source === 'library');
    if (page && !page.hasMore) {
      return DEFAULT_EMPTY_RESULT;
    }

    const offset = page ? page.nextOffset : 0;
    const requestBody = {
      searchTerms: query,
      limit: DEFAULT_PAGE_SIZE / 2,
      offset,
      nextPage: nextPage || null,
      facetSize: 50
    };
    if (filters) {
      requestBody.facetFilters = filters;
    }
    const options = getOptions('POST', JSON.stringify(requestBody));
    options.headers['Authorization'] = await currentUserAccessToken();
    return fetchWithReAuthLib('/api/vsc/image/search', options).catch(err => {
      if (err.status) {
        Sentry.captureException(new Error(`library search failed: ${err.status} - ${err.message}`));
      }
      throw err;
    });
  };

  const [streamlineResult, libraryResult] = await Promise.allSettled([fetchStreamlineSearch(), fetchLibrarySearch()]);

  if (streamlineResult.status === 'rejected' && libraryResult.status === 'rejected') {
    throw new Error(libraryResult.reason);
  }

  const streamlineResults = streamlineResult.status === 'fulfilled' ? streamlineResult.value : DEFAULT_EMPTY_RESULT;
  const libraryResults = libraryResult.status === 'fulfilled' ? libraryResult.value : DEFAULT_EMPTY_RESULT;

  const libraryFacets = skipLibrary
    ? []
    : (libraryResults.data[0].facets || []).map(f => ({ ...f, source: 'library' }));
  const streamlineFacets = skipStreamline
    ? []
    : (streamlineResults.data[0].facets || []).map(f => ({ ...f, source: 'streamline' }));

  return {
    data: [
      {
        images: [...libraryResults.data[0].images, ...streamlineResults.data[0].images],
        facets: [...libraryFacets, ...streamlineFacets]
      }
    ],
    meta: {
      count: libraryResults.meta.count,
      total: libraryResults.meta.total
    },
    pagination: [
      {
        source: 'library',
        offset: libraryResults.meta.offset,
        nextOffset: libraryResults.meta.nextOffset,
        total: libraryResults.meta.total,
        hasMore: libraryResults.meta.hasMore
      },
      {
        source: 'streamline',
        offset: streamlineResults.meta.offset,
        nextOffset: streamlineResults.meta.nextOffset,
        total: streamlineResults.meta.total,
        hasMore: streamlineResults.meta.hasMore
      }
    ]
  };
};
